import { Avatar, Dropdown, Icon, Menu } from 'antd';
import classNames from 'classnames';
import * as React from 'react';
import injectSheet from 'react-jss';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { closeSideMenu } from 'src/actions/navigationActions';
import { setHomePage } from 'src/api/users';
import { NAVBAR_ZINDEX } from 'src/globals';
import { RouterProps, StylesProps, valueof } from 'src/Library/Types';
import * as partsConstants from 'src/utils/partsToShow/partsConstants';
import { getPartsMap, GetPartsMapReturnType } from 'src/utils/partsToShow/partsMap';
import * as sidebarSectionConstants from 'src/utils/partsToShow/sidebarSectionConstants';
import { getSideMenuList } from 'src/utils/partsToShow/sideMenuList';
import { RouteMapping } from 'src/utils/utils';
import { tokens } from 'src/design-system';

const styles = theme => ({
  parentDiv: {
    backgroundColor: '#051E36',
    minWidth: 300,
    maxWidth: 400,
    cursor: 'pointer',
    minHeight: 154,
    padding: '16px 24px 0px',
  },
  body: {
  },
  userDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 25,
  },
  userNameAndEmail: {
    minWidth: 198,
    maxWidth: 300,
    padding: '16px 24px',
  },
  userName: {
    fontSize: 16,
    color: tokens.colors.bgWhite,
    fontWeight: 600,
    marginBottom: 10,
    lineHeight: 1,
  },
  userEmail: {
    fontSize: 10,
    color: tokens.colors.bgWhite,
    fontWeight: 600,
    lineHeight: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  subMenu: {
    color: tokens.colors.bgWhite,
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    padding: '0 0 0 12px',
    lineHeight: 1,
    marginBottom: 25,
  },
  homeSubMenu: {
    color: tokens.colors.bgWhite,
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    padding: '0 0 0 12px',
    lineHeight: 1,
    marginBottom: 25,
    cursor: 'pointer',
    '&.disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  logoutSubMenu: {
    color: tokens.colors.error,
    borderTop: `1px solid ${tokens.colors.bgSubtle}`,
    padding: '16px 0px 25px 12px',
  },
  icon: {
    width: 14,
    marginRight: 8,
    color: tokens.colors.bgWhite,
  },
  redColor: {
    color: tokens.colors.error,
  },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, PropsFromRedux, RouterProps {}

const UserAccountButton: React.FC<IProps> = (props) => {
  const {
    type,
    classes,
    userData,
    partsToShow,
    closeSideMenu,
    permissionsModule,
    history,
    orgType,
    showProcurement,
    defaultLandingPage,
    userLandingPage,
    discoverRatesPrefillData,
  } = props;

  const [isSettingHome, setIsSettingHome] = React.useState(false);

  const currentPath = history.location.pathname;

  const RouteMappingObj = React.useMemo(() => {
    const viewType = showProcurement ? 'buy' : 'sell';
    const redirectToSpotRatesLandingPage = orgType === 'SHIPPER' && !discoverRatesPrefillData;
    const routeParams = {
      viewType,
      redirectToSpotRatesLandingPage
    };
    return RouteMapping(routeParams);
  }, [showProcurement, orgType, discoverRatesPrefillData]);
  
  // Use userLandingPage if available, otherwise fallback to defaultLandingPage
  const activeLandingPage = userLandingPage || defaultLandingPage;
  
  // Check if current path matches the active landing page route
  const isOnActiveLandingPage = activeLandingPage && 
    RouteMappingObj[activeLandingPage] && 
    currentPath === RouteMappingObj[activeLandingPage];

  const handleSetHomePage = async () => {
    if (isOnActiveLandingPage || isSettingHome ) {
      return;
    }

    setIsSettingHome(true);

    try {
      // Find the route key for the current path
      const routeKey = Object.keys(RouteMappingObj).find(
        key => RouteMappingObj[key] === currentPath
      );

      if (routeKey) {
        const body = { page: routeKey };
        const response = await setHomePage(body);
        
        if (response.isSuccess) {
          // Mark as successfully set and close the dropdown
          window.location.reload();
        } else {
          console.error('Failed to set home page:', response.errorMessage);
        }
      }
    } catch (error) {
      console.error('Error setting home page:', error);
    } finally {
      setIsSettingHome(false);
    }
  };

  const renderUserDetails = () => {
    return (
      <div className={classes.userDetails}>
        <div>
          <Avatar size={60} style={{ fontSize: 24 }}>{userData.name?.[0].toUpperCase()}</Avatar>
        </div>
        <div className={classes.userNameAndEmail}>
          <div className={classes.userName}>{userData.name}</div>
          <div className={classes.userEmail}>{userData.email}</div>
        </div>
      </div>
    );
  }

  const sectionList = getSideMenuList;
  const partsMap = getPartsMap(permissionsModule, type);

  const handleRedirect = (pathname: string, rootTitle: string) => {
    return {
      pathname,
      state: {
        rootTitle,
      }
    };
  }

  const renderIcon = (id: valueof<typeof partsConstants>) => {
    switch (id) {
      case partsConstants.LOGOUT:
        return <Icon type="poweroff" style={{ marginRight: 8, color: tokens.colors.error }} />;
      case partsConstants.MY_ORGANISATION:
        return <Icon type="idcard" style={{ marginRight: 8, color: tokens.colors.bgWhite }} />;
      case partsConstants.MY_PROFILE:
        return <Icon type="user" style={{ marginRight: 8, color: tokens.colors.bgWhite }} />;
      case partsConstants.MY_SUBSCRIPTION_PLAN:
        return <Icon type="container" style={{ marginRight: 8, color: tokens.colors.bgWhite }} />;
      case partsConstants.SETUP_USERS:
        return <Icon type="team" style={{ marginRight: 8, color: tokens.colors.bgWhite }} />
      default:
        return <div className={classes.icon} />;
    }
  }

  const renderSubMenu = (id: valueof<typeof partsConstants>, item: valueof<GetPartsMapReturnType>) => {
    const isLogoutSubMenu = id === partsConstants.LOGOUT;
    return (
      <Link
        to={() => handleRedirect(item.route, item.rootTitle)}
        onClick={closeSideMenu}
      >
        <div
          className={classNames(classes.subMenu, isLogoutSubMenu && classes.logoutSubMenu)}
          key={id}
        >
          {renderIcon(id)}
          {item.sectionTitle}
        </div>
      </Link>
    );
  }

  const renderHomeButton = () => {
    // Check if current route exists in RouteMapping
    const routeValues = Object.values(RouteMappingObj);
    const isValidRoute = routeValues.includes(currentPath);
    
    // Only show home button for valid routes
    if (!isValidRoute) {
      return null;
    }
        const isDisabled = isOnActiveLandingPage;
    return (
        <div
          className={classNames(classes.homeSubMenu, isDisabled && 'disabled')}
          onClick={isDisabled ? undefined : handleSetHomePage}
        >
          <Icon
            type="home"
            theme={isDisabled ? "filled" : "outlined"}
            spin={isSettingHome}
            style={{ marginRight: 8, color: tokens.colors.bgWhite }}
          />
          Set as Home Page
        </div>
    );
  }

  const renderAccountMenu = () => {
    const accountMenu = sectionList.find(section => section.id === sidebarSectionConstants.MY_ACCOUNT);

    if (!accountMenu) {
      return null;
    }

    return accountMenu.options.map((subMenuId) => {
      const subMenu = partsMap[subMenuId];

      if (!partsToShow[subMenuId] || !subMenu) {
        return null;
      }

      return renderSubMenu(subMenuId, subMenu);
    });
  }

  const accountMenu = () => {
    return (
      <Menu className={classes.parentDiv}>
        {renderUserDetails()}
        <div className={classes.body}>
          {renderHomeButton()}
          {renderAccountMenu()}
        </div>
      </Menu>
    );
  }

  return (
    <Dropdown overlay={accountMenu} trigger={['click']} overlayStyle={{ zIndex: NAVBAR_ZINDEX + 3 }}>
      <div style={{ cursor: 'pointer' }}>
        {<Avatar size={25} >{userData.name?.[0]?.toUpperCase()}</Avatar>} <Icon type="down" />
      </div>
    </Dropdown>
  );
}

const mapStateToProps = ({ masterData, authUser, navigation }) => ({
  userData: masterData.userData,
  partsToShow: authUser.partsToShow,
  type: masterData.userData.type,
  permissionsModule: masterData.modules,
  orgType: masterData.userData.type,
  showProcurement: masterData.modules?.show_inquiry || masterData.modules?.show_inquiry_bidding,
  defaultLandingPage: masterData?.modules?.default_route || '',
  userLandingPage: masterData?.userData?.user_metadata?.landing_page || '',
  discoverRatesPrefillData: masterData?.redirectPrefillData,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  closeSideMenu,
}, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default withRouter(connector(injectSheet(styles)(UserAccountButton)));